/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { AttributeSwatch, ProductSearchResult, SearchSuggestion, SearchSuggestionCriteria } from '@msdyn365-commerce/retail-proxy';
import { getSearchSuggestionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import {
    DimensionTypes,
    ArrayExtensions,
    generateImageUrl,
    generateSwatchImageUrl,
    getProductPageUrlSync
} from '@msdyn365-commerce-modules/retail-actions';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export enum SuggestionType {
    Category = 'ScopedCategory',
    Keyword = 'Keyword',
    Product = 'Product',
    None = 'None'
}

/**
 * Input class for auto suggest search input.
 */
export class AutoSuggestInput implements IActionInput {
    public searchQuery?: string;

    public topResultsCount?: number;

    public suggestionType?: string;

    public hitPrefix?: string;

    public hitSuffix?: string;

    public constructor(searchText?: string, top?: number, suggestionType?: string, hitPrefix?: string, hitSuffix?: string) {
        this.searchQuery = searchText;
        this.topResultsCount = top;
        this.suggestionType = suggestionType;
        this.hitPrefix = hitPrefix;
        this.hitSuffix = hitSuffix;
    }

    public getCacheKey = () => 'AutoSuggestSearchSuggestions';

    public getCacheObjectType = () => 'AutoSuggestSearchSuggestions';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new AutoSuggestInput();
};

/**
 * Calls the Retail API and returns a auto-suggest suggestions.
 * @param input
 * @param ctx
 */
export async function getSearchSuggestionsAction(input: AutoSuggestInput, ctx: IActionContext): Promise<Autosuggestions | null> {
    // If no input is provided fail out
    if (!input || !input.searchQuery) {
        throw new Error('[getSearchSuggestionsAction]No valid Input was provided, failing');
    }

    const autosuggest: Autosuggestions = {};

    const autoSuggestPromises = [_getAutoSuggest(input, autosuggest, ctx)];
    return Promise.all(autoSuggestPromises).then(() => {
        return autosuggest;
    });
}

async function _getAutoSuggest(input: AutoSuggestInput, autosuggest: Autosuggestions, ctx: IActionContext): Promise<void> {
    const catalogId = getCatalogId(ctx.requestContext);
    const searchCriteria: SearchSuggestionCriteria = {
        ProductSearchCriteria: {
            SearchCondition: input.searchQuery && input.searchQuery.length > 0 ? input.searchQuery : '',
            Context: {
                ChannelId: +ctx.requestContext.apiSettings.channelId,
                CatalogId: catalogId
            }
        },
        HitPrefix: input.hitPrefix,
        HitSuffix: input.hitSuffix,
        SuggestionType: input.suggestionType
    };

    const searchSuggestions = await getSearchSuggestionsAsync(
        { callerContext: ctx, queryResultSettings: { Paging: { Top: input.topResultsCount } } },
        searchCriteria
    );

    if (searchSuggestions && searchSuggestions.length > 0) {
        const searchResults = await searchByCriteriaAsync(
            {
                callerContext: ctx,
                queryResultSettings: {
                    Paging: { Skip: 0, Top: searchSuggestions.length }
                }
            },
            {
                Ids: searchSuggestions
                    .filter(item => item.Id !== undefined)
                    .map((item: SearchSuggestion) => {
                        return Number(item.Id);
                    }),
                Context: {
                    ChannelId: +ctx.requestContext.apiSettings.channelId,
                    CatalogId: catalogId
                },
                IncludeAttributes: true
            }
        );
        autosuggest.AllSearchResults = searchSuggestions.map(item => {
            if (item.SuggestionType === SuggestionType.Product) {
                const searchResult = searchResults.find(result => result.RecordId === Number(item.Id));
                const imageUrl = searchResult
                    ? getProductImageUrlFromDefaultColorSwatch(ctx, searchResult)
                    : item.ImageUrl
                    ? generateImageUrl(item.ImageUrl, ctx.requestContext.apiSettings)
                    : '';
                item.ImageUrl = imageUrl;

                const swatchColor = getDefaultSwatchSelected(searchResult, 'color')?.SwatchValue;
                if (swatchColor && item.Value) {
                    const url = getProductPageUrlSync(item.Value, (item.Id as unknown) as number, ctx, undefined);
                    item.Value += `|${url}?color=${encodeURIComponent(swatchColor)}`;
                }
            }
            return item;
        });

        autosuggest.AllSearchResults = searchSuggestions;
    } else {
        ctx.telemetry.error(
            `[getSearchSuggestionsAction] unable to get availabilites for search with suggestion type ${searchCriteria.SuggestionType}`
        );
    }
}

function getDefaultSwatchSelected(productData?: ProductSearchResult, attribute?: string): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const colorAttribute = productData.AttributeValues.find(attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === attribute);
    if (!ArrayExtensions.hasElements(colorAttribute?.Swatches)) {
        return null;
    }

    const defaultSwatch = colorAttribute!.Swatches.find(item => item.IsDefault === true) ?? colorAttribute!.Swatches[0];
    return defaultSwatch;
}
function getProductImageUrlFromDefaultColorSwatch(coreContext: IActionContext, productData?: ProductSearchResult): string | undefined {
    const dimensionToPreSelectInProductCard = coreContext.requestContext.app.config.dimensionToPreSelectInProductCard;
    if (dimensionToPreSelectInProductCard === DimensionTypes.none) {
        return productData?.PrimaryImageUrl;
    }

    // image URL building for swatch taken from swatch-item.tsx component, but using attributes instead of dimensions
    const swatchSize = getDefaultSwatchSelected(productData, 'size')?.SwatchValue?.replace('/', '^');
    const swatchColor = getDefaultSwatchSelected(productData, 'color')?.SwatchValue?.replace('/', '^');
    const swatchImage = `Products/${productData?.ItemId} ^  ^ ${swatchSize} ^ ${swatchColor} ^ None_000_000_swatch.jpg`;
    const fullImageUrl = `${generateSwatchImageUrl(swatchImage, coreContext.requestContext.apiSettings)}&h=50&q=80&m=6`;
    return fullImageUrl;
}

export const getSearchSuggestionsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/search/get-auto-suggest-suggestions',
    action: <IAction<Autosuggestions>>getSearchSuggestionsAction,
    input: createInput
});

export default getSearchSuggestionsActionDataAction;
